/**
 * Client duplicate of src/lib/redemption-period.ts. If you change one,
 * change the other, a divergence would show a countdown that disagrees
 * with the server's cap.
 */

export type BenefitPeriod = "ONE_OFF" | "WEEKLY" | "MONTHLY" | "QUARTERLY" | "YEARLY";

export function computePeriodStart(anchor: Date, now: Date, period: BenefitPeriod): Date {
  switch (period) {
    case "ONE_OFF":
      return new Date(0);
    case "WEEKLY":
      return weeklyPeriodStart(anchor, now);
    case "MONTHLY":
      return monthlyPeriodStart(anchor, now, 1);
    case "QUARTERLY":
      return monthlyPeriodStart(anchor, now, 3);
    case "YEARLY":
      return monthlyPeriodStart(anchor, now, 12);
  }
}

export function computeNextPeriodStart(
  anchor: Date,
  now: Date,
  period: BenefitPeriod,
): Date | null {
  if (period === "ONE_OFF") return null;
  const current = computePeriodStart(anchor, now, period);
  if (period === "WEEKLY") {
    return new Date(current.getTime() + 7 * 86_400_000);
  }
  const step = period === "MONTHLY" ? 1 : period === "QUARTERLY" ? 3 : 12;
  return clampToMonthEnd(
    current.getUTCFullYear(),
    current.getUTCMonth() + step,
    anchor.getUTCDate(),
    anchor,
  );
}

function monthlyPeriodStart(anchor: Date, now: Date, monthStep: number): Date {
  const anchorDay = anchor.getUTCDate();
  const nowY = now.getUTCFullYear();
  const nowM = now.getUTCMonth();
  for (let back = 0; back < monthStep * 12 * 100; back += monthStep) {
    const cand = clampToMonthEnd(nowY, nowM - back, anchorDay, anchor);
    if (cand.getTime() <= now.getTime()) return cand;
  }
  return anchor;
}

function weeklyPeriodStart(anchor: Date, now: Date): Date {
  const step = 7 * 86_400_000;
  const diff = now.getTime() - anchor.getTime();
  if (diff < 0) return anchor;
  const periodsElapsed = Math.floor(diff / step);
  return new Date(anchor.getTime() + periodsElapsed * step);
}

function clampToMonthEnd(
  year: number,
  monthIndex: number,
  day: number,
  anchorForTime: Date,
): Date {
  const normalised = new Date(Date.UTC(year, monthIndex, 1));
  const lastDay = new Date(
    Date.UTC(normalised.getUTCFullYear(), normalised.getUTCMonth() + 1, 0),
  ).getUTCDate();
  const clamped = Math.min(day, lastDay);
  return new Date(
    Date.UTC(
      normalised.getUTCFullYear(),
      normalised.getUTCMonth(),
      clamped,
      anchorForTime.getUTCHours(),
      anchorForTime.getUTCMinutes(),
      anchorForTime.getUTCSeconds(),
    ),
  );
}

// -- Legacy shims for pre-2026-09-09 callers.
export function currentPeriodStart(anchor: Date, now: Date): Date {
  return computePeriodStart(anchor, now, "MONTHLY");
}
export function nextPeriodStart(anchor: Date, now: Date): Date {
  return computeNextPeriodStart(anchor, now, "MONTHLY")!;
}

/**
 * Human-readable countdown. Examples: "1d 12h 3m" / "3h 27m" / "42m" / "Ready".
 */
export function formatCountdown(targetMs: number, nowMs: number): string {
  const diff = targetMs - nowMs;
  if (diff <= 0) return "Ready";
  const totalMinutes = Math.floor(diff / 60_000);
  const d = Math.floor(totalMinutes / (60 * 24));
  const h = Math.floor((totalMinutes % (60 * 24)) / 60);
  const m = totalMinutes % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

/** Human label for a period, e.g. "monthly" / "weekly" / "one-off". */
export function periodLabel(period: BenefitPeriod): string {
  switch (period) {
    case "ONE_OFF":
      return "one-off";
    case "WEEKLY":
      return "weekly";
    case "MONTHLY":
      return "monthly";
    case "QUARTERLY":
      return "quarterly";
    case "YEARLY":
      return "yearly";
  }
}
